// API endpoint constants — single source of truth for paths.
//
// All paths are relative to apiClient's baseURL (default /api/v1).

export const endpoints = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    deviceToken: '/auth/device-token',
    passwordResetRequest: '/auth/password/reset-request',
    passwordReset: '/auth/password/reset',
    // Weighbridge Kiosk Mode — PIN login + unauthenticated staff picker.
    kioskLogin: '/auth/kiosk-login',
    kioskStaff: '/auth/kiosk-staff',
  },
  // Weighbridge Kiosk Mode — staff self-service PIN management.
  staff: {
    pinStatus: '/staff/me/pin',
    setPin: '/staff/me/pin',
    removePin: '/staff/me/pin',
  },
  // Wave I — platform super-admin auth (separate from tenant staff auth).
  superAuth: {
    login: '/auth/super/login',
    refresh: '/auth/super/refresh',
    logout: '/auth/super/logout',
  },
  // System logging — super-admin log viewer + retention + platform SMTP.
  // Mirrors server/src/modules/systemLogs/logs.controller.ts.
  systemLogs: {
    list: '/super/logs',
    delete: '/super/logs',
    retention: '/super/logs/retention',
    updateRetention: (module: string, severity: string) =>
      `/super/logs/retention/${encodeURIComponent(module)}/${encodeURIComponent(severity)}`,
    platformSmtp: '/super/platform-smtp',
  },
  company: {
    theme: '/company/theme',
  },
  notifications: {
    // Wave F — staff push subscriptions land via POST /devices. The legacy
    // pushSubscribe path is kept for reference but is not the canonical one.
    pushSubscribe: '/notifications/push/subscribe',
    queue: '/notifications/queue',
    queueSummary: '/notifications/queue/summary',
    flush: '/notifications/flush',
  },
  // Collections notification bell — real-time actionable counts (receipts
  // needing action + cancellation requests) via SSE, plus a REST seed.
  collectionsNotifications: {
    stream: '/collections/notifications/stream',
    counts: '/collections/notifications/counts',
  },
  // Wave F — staff-realm device registration for web push.
  devices: {
    list: '/devices',
    register: '/devices',
    revoke: '/devices',
  },
  // Location Tracking — Phase 4. Latest known position per driver +
  // per-driver location history for a given day. Server mounts these
  // under /api/v1/drivers/locations.
  driverLocations: {
    latest: '/drivers/locations',
    history: (encDriverId: string) =>
      `/drivers/locations/${encodeURIComponent(encDriverId)}/history`,
  },
  staffLocations: {
    latest: '/staff/locations',
  },
  drivers: {
    list: '/drivers',
    create: '/drivers',
    detail: (uuid: string) => `/drivers/${uuid}`,
    update: (uuid: string) => `/drivers/${uuid}`,
    delete: (uuid: string) => `/drivers/${uuid}`,
    setStatus: (uuid: string) => `/drivers/${uuid}/status`,
    holidays: (uuid: string) => `/drivers/${uuid}/holidays`,
    bookings: (uuid: string) => `/drivers/${uuid}/bookings`,
  },
  // Holidays — top-level management page (mirrors legacy
  // `operations/manage_holidays.php`). Aggregates staff_holidays and
  // driver_holidays. Distinct from the per-entity nested endpoints.
  holidays: {
    list: '/holidays',
    create: '/holidays',
    update: (uuid: string) => `/holidays/${uuid}`,
    delete: (uuid: string) => `/holidays/${uuid}`,
    bulkImport: '/holidays/bulk-bank-holidays',
  },
  // Wave E — admin / settings staff (users) management.
  users: {
    list: '/users',
    create: '/users',
    detail: (uuid: string) => `/users/${uuid}`,
    update: (uuid: string) => `/users/${uuid}`,
    delete: (uuid: string) => `/users/${uuid}`,
    restore: (uuid: string) => `/users/${uuid}/restore`,
    passwordReset: (uuid: string) => `/users/${uuid}/password-reset`,
    sessions: (uuid: string) => `/users/${uuid}/sessions`,
    holidays: (uuid: string) => `/users/${uuid}/holidays`,
  },
  // Wave E — message templates.
  templates: {
    list: '/message-templates',
    create: '/message-templates',
    detail: (uuid: string) => `/message-templates/${uuid}`,
    update: (uuid: string) => `/message-templates/${uuid}`,
    delete: (uuid: string) => `/message-templates/${uuid}`,
    render: (uuid: string) => `/message-templates/${uuid}/render`,
    preview: (uuid: string) => `/message-templates/${uuid}/preview`,
  },
  // Wave E — company settings (info / branding / notifications).
  companySettings: {
    overview: '/company',
    update: '/company',
    list: '/company/settings',
    upsert: (key: string) => `/company/settings/${encodeURIComponent(key)}`,
  },
  // Communication settings — email / SMS / preferences / parsers
  // (server module: settings/communication, mounted at
  //  /api/v1/settings/communication).
  communication: {
    EMAIL_LIST: '/settings/communication/email',
    EMAIL: (purpose: string) =>
      `/settings/communication/email/${encodeURIComponent(purpose)}`,
    EMAIL_TEST: (purpose: string) =>
      `/settings/communication/email/${encodeURIComponent(purpose)}/test`,
    SMS: '/settings/communication/sms',
    SMS_TEST: '/settings/communication/sms/test',
    PREFERENCES: '/settings/communication/preferences',
    IMPORT_PARSERS: '/settings/communication/import/parsers',
  },
  // Wave E — reference-data CRUD via the per-table admin paths.
  // Reads still come from /reference-data/{products,work-sources,payment-modes}
  // and /time-slots — create/update/delete sit on the parent paths.
  timeSlots: {
    list: (type: 'weekday' | 'weekend') => `/collections/time-slots?type=${type}` as const,
    create: '/collections/time-slots',
    update: (id: number) => `/collections/time-slots/${id}`,
    delete: (id: number) => `/collections/time-slots/${id}`,
  },
  products: {
    list: '/reference-data/products',
    create: '/products',
    update: (id: number) => `/products/${id}`,
    delete: (id: number) => `/products/${id}`,
  },
  workSources: {
    list: '/reference-data/work-sources',
    create: '/work-sources',
    update: (id: number) => `/work-sources/${id}`,
    delete: (id: number) => `/work-sources/${id}`,
  },
  paymentModes: {
    list: '/reference-data/payment-modes',
    create: '/payment-modes',
    update: (id: number) => `/payment-modes/${id}`,
    delete: (id: number) => `/payment-modes/${id}`,
  },
  diary: {
    week: '/collections/diary',
    notes: '/collections/diary/notes',
    note: (encId: string) => `/collections/diary/notes/${encId}`,
    reorder: '/collections/diary/reorder',
  },
  bookings: {
    list: '/collections/bookings',
    detail: (encId: string) => `/collections/bookings/${encId}`,
    checkDuplicate: '/collections/bookings/check-duplicate',
    /** Static path — server matches `/cancel` BEFORE `/:enc_id`. Body
     *  carries `{ unique_id, reason, destination }`. */
    cancel: '/collections/bookings/cancel',
    importEmailInbox: '/collections/bookings/import-email/inbox',
    importEmailParse: '/collections/bookings/import-email/parse',
    declineCancellation: (encId: string) => `/collections/bookings/${encId}/decline-cancellation`,
    reinstate: (encId: string) => `/collections/bookings/${encId}/reinstate`,
    acknowledgeCompletedAfterCancellation: (encId: string) =>
      `/collections/bookings/${encId}/acknowledge-completed-after-cancellation`,
    /** Customer-self-cancellation link — POST issues / refreshes a token
     *  (returns the raw URL exactly once), GET reports only whether a
     *  link is currently active (the raw token is never re-readable). */
    cancellationLink: (encId: string) => `/collections/bookings/${encId}/cancellation-link`,
  },
  // Collections-wide cross-table search (bookings + pending + receipts)
  // powering the Booking Search tab on the System Lookup page.
  collections: {
    searchBookings: '/collections/search/bookings',
  },
  pending: {
    list: '/collections/pending',
    create: '/collections/pending',
    detail: (encId: string) => `/collections/pending/${encId}`,
    update: (encId: string) => `/collections/pending/${encId}`,
    delete: (encId: string) => `/collections/pending/${encId}`,
    convert: (encId: string) => `/collections/pending/${encId}/convert`,
    notes: (encId: string) => `/collections/pending/${encId}/notes`,
    followup: (encId: string) => `/collections/pending/${encId}/followup`,
    finalNotice: (encId: string) => `/collections/pending/${encId}/final-notice`,
    incorrectContact: (encId: string) => `/collections/pending/${encId}/incorrect-contact`,
    cancel: (encId: string) => `/collections/pending/${encId}/cancel`,
  },
  customers: {
    list: '/customers',
    create: '/customers',
    /** Kiosk quick-create (name + type only). Returns a customer flagged
     *  is_complete=false; completion of its WB tickets is gated until the
     *  record is filled in on the main module. */
    quickCreate: '/customers/quick',
    detail: (uuid: string) => `/customers/${uuid}`,
    update: (uuid: string) => `/customers/${uuid}`,
    delete: (uuid: string) => `/customers/${uuid}`,
    restore: (uuid: string) => `/customers/${uuid}/restore`,
    merge: '/customers/merge',
    bookings: (uuid: string) => `/customers/${uuid}/bookings`,
    bankHistory: (uuid: string) => `/customers/${uuid}/bank-detail-changes`,
    skipJobs: (uuid: string) => `/customers/${uuid}/skip-jobs`,
    addBranch: (uuid: string) => `/customers/${uuid}/branches`,
    removeBranch: (uuid: string, branch: string) => `/customers/${uuid}/branches/${branch}`,
  },
  receipts: {
    list: '/collections/receipts',
    create: '/collections/receipts',
    detail: (encId: string) => `/collections/receipts/${encId}`,
    update: (encId: string) => `/collections/receipts/${encId}`,
    delete: (encId: string) => `/collections/receipts/${encId}`,
    pdf: (encId: string) => `/collections/receipts/${encId}/pdf`,
    approve: (encId: string) => `/collections/receipts/${encId}/approve`,
    reject: (encId: string) => `/collections/receipts/${encId}/reject`,
    archive: (encId: string) => `/collections/receipts/${encId}/archive`,
    send: (encId: string) => `/collections/receipts/${encId}/send`,
    photos: (encId: string) => `/collections/receipts/${encId}/photos`,
    photo: (encId: string, photoEncId: string) => `/collections/receipts/${encId}/photos/${photoEncId}`,
    checkDuplicate: '/collections/receipts/check-duplicate',
    bulkSend: '/collections/receipts/bulk-send',
    sendAllUnsent: '/collections/receipts/send-all-unsent',
    unsentCount: '/collections/receipts/unsent-count',
    bankDetailsCheck: '/collections/receipts/bank-details-check',
  },
  activityLog: {
    list: '/activity-log',
  },
  reports: {
    /** Registry list — `{ reports: [...] }`. */
    list: '/reports',
    /** Run a registered report by key. */
    run: (reportKey: string) => `/reports/${encodeURIComponent(reportKey)}`,
    /** Automated Reminders tab data — see server reminders.service. */
    reminders: '/reports/reminders',
  },
  drafts: {
    list: '/collections/drafts',
    detail: (encId: string) => `/collections/drafts/${encId}`,
  },
  // Reference-data list endpoints.
  // products/workSources/paymentModes: not yet server-side (404 silent fallback).
  // timeSlots: live on server (migration 069 / pre-Wave-C). Falls back to
  //   static ['AM','AM-PM','PM'] if the server returns 404 (forward-compat).
  referenceData: {
    products: '/reference-data/products',
    workSources: '/reference-data/work-sources',
    paymentModes: '/reference-data/payment-modes',
    timeSlots: (type: 'weekday' | 'weekend') => `/collections/time-slots?type=${type}` as const,
  },
  // Wave D — Invoices.
  invoices: {
    list: '/collections/invoices',
    create: '/collections/invoices',
    detail: (encId: string) => `/collections/invoices/${encId}`,
    update: (encId: string) => `/collections/invoices/${encId}`,
    delete: (encId: string) => `/collections/invoices/${encId}`,
    pdf: (encId: string) => `/collections/invoices/${encId}/pdf`,
    send: (encId: string) => `/collections/invoices/${encId}/send`,
    /** Work-source dropdown for the list-page filter. */
    workSources: '/collections/invoices/work-sources',
    /** SCC invoice compare — multipart upload of the supplier PDF. */
    compare: '/collections/invoices/compare',
  },
  // Scrap Quote Generator — ephemeral quotes with email-to-customer flow.
  quotes: {
    generate: '/collections/quotes/generate',
    send: (uuid: string) => `/collections/quotes/${uuid}/send`,
  },
  // Wave D — BACS payment runs.
  bacs: {
    list: '/collections/bacs',
    create: '/collections/bacs',
    unpaid: '/collections/bacs/unpaid',
    /** Payment Run History tab — all paid receipts (any method). */
    paid: '/collections/bacs/paid',
    /** Mark non-BACS receipts (cheque / Bread4Scrap / free collect) as paid. */
    markPaid: '/collections/bacs/mark-paid',
    detail: (encId: string) => `/collections/bacs/${encId}`,
    voidRun: (encId: string) => `/collections/bacs/${encId}`,
    submit: (encId: string) => `/collections/bacs/${encId}/submit`,
    exportFile: (encId: string) => `/collections/bacs/${encId}/export`,
    /** One-step "Generate BACS file": template CSV + run + receipts paid, in one transaction. */
    generate: '/collections/bacs/generate',
    generatePreview: '/collections/bacs/generate/preview',
    files: '/collections/bacs/files',
    downloadFile: (encId: string) => `/collections/bacs/files/${encId}/download`,
  },
  // Wave D — QuickBooks integration.
  quickbooks: {
    status: '/collections/integrations/quickbooks/status',
    auth: '/collections/integrations/quickbooks/auth',
    disconnect: '/collections/integrations/quickbooks/disconnect',
    sync: '/collections/integrations/quickbooks/sync',
    syncLog: '/collections/integrations/quickbooks/sync/log',
    // Grouped sync (Batch 6)
    syncEligible: '/collections/integrations/quickbooks/sync/eligible',
    syncRecords: '/collections/integrations/quickbooks/sync/records',
    syncGroup: '/collections/integrations/quickbooks/sync/group',
    mappings: '/collections/integrations/quickbooks/mappings',
    mapping: (encId: string) => `/collections/integrations/quickbooks/mappings/${encId}`,
    settings: '/collections/integrations/quickbooks/settings',
    resetAccounts: '/collections/integrations/quickbooks/settings/reset-accounts',
    settingsAccounts: '/collections/integrations/quickbooks/settings/accounts',
    settingsValidate: '/collections/integrations/quickbooks/settings/validate',
  },
  collectionsSettings: {
    get: '/collections/company-settings',
    update: '/collections/company-settings',
  },
  // Subdomain rebuild — public unauthenticated endpoints. Used by the
  // CancelBookingPage at `/cancel/:token`. Server validates the token
  // (decrypts to company_id internally); no JWT is sent.
  publicBookings: {
    cancel: (token: string) => `/public/bookings/cancel/${encodeURIComponent(token)}`,
  },
  // Stage 5 — Skip Management module. Mirrors the server mount points
  // under /api/v1/skip/*. The job-action endpoints (collection-request /
  // exchange-request / tip-return-request / postpone / complete-* — see
  // SKIP_MODULE_PLAN.md §7.1) ship in Skip v2; the JOB_ACTION builder is
  // declared now so the client API surface is forward-compatible.
  skipJobs: {
    list: '/skip/jobs',
    detail: (encId: string) => `/skip/jobs/${encId}`,
    create: '/skip/jobs',
    update: (encId: string) => `/skip/jobs/${encId}`,
    delete: (encId: string) => `/skip/jobs/${encId}`,
    status: (encId: string) => `/skip/jobs/${encId}/status`,
    priority: (encId: string) => `/skip/jobs/${encId}/priority`,
    notes: (encId: string) => `/skip/jobs/${encId}/notes`,
    attachments: (encId: string) => `/skip/jobs/${encId}/attachments`,
    /** DELETE single attachment by its enc_id. Soft-delete; the MinIO
     *  object stays put (matches scrap-bookings policy). */
    attachment: (encId: string, attachmentEncId: string) =>
      `/skip/jobs/${encId}/attachments/${attachmentEncId}`,
    /** Active actions list — jobs with any sub-flag in 'requested' state. */
    activeActions: '/skip/jobs/active-actions',
    /** Phase 2b — dynamic action discovery for the detail page. */
    availableActions: (encId: string) => `/skip/jobs/${encId}/available-actions`,
    /** Workflow actions per §7.1 + Phase 2b /actions/* renames. */
    action: (encId: string, action: string) => `/skip/jobs/${encId}/${action}`,
    /** Feature 1 — roll the job back to the previous status from
     *  the audit log. Refused for collected/completed/cancelled. */
    revertStatus: (encId: string) => `/skip/jobs/${encId}/revert-status`,
  },
  skipEnquiries: {
    list: '/skip/enquiries',
    detail: (encId: string) => `/skip/enquiries/${encId}`,
    create: '/skip/enquiries',
    update: (encId: string) => `/skip/enquiries/${encId}`,
    delete: (encId: string) => `/skip/enquiries/${encId}`,
    convert: (encId: string) => `/skip/enquiries/${encId}/convert`,
    cancel: (encId: string) => `/skip/enquiries/${encId}/cancel`,
    /** Contacts log per §7.2. Endpoints not yet built server-side. */
    contacts: (encId: string) => `/skip/enquiries/${encId}/contacts`,
    /** Feature 2 — jobs with any sub-flag in 'requested' state.
     *  Returns from skip_jobs (NOT skip_enquiries) — the URL lives
     *  under /skip/enquiries because the result lands on the
     *  Pending Requests tab of the enquiries page. */
    pendingRequests: '/skip/enquiries/pending-requests',
    /** Skip Module v2 — enquiry attachments. Mirrors the jobs
     *  attachment endpoints (GET / POST / DELETE). */
    attachments: (encId: string) => `/skip/enquiries/${encId}/attachments`,
    attachment: (encId: string, attachmentEncId: string) =>
      `/skip/enquiries/${encId}/attachments/${attachmentEncId}`,
  },
  skipInventory: {
    list: '/skip/inventory',
    detail: (encId: string) => `/skip/inventory/${encId}`,
    create: '/skip/inventory',
    update: (encId: string) => `/skip/inventory/${encId}`,
    delete: (encId: string) => `/skip/inventory/${encId}`,
    adjust: (encId: string) => `/skip/inventory/${encId}/adjust`,
    /** Phase 6H — full audit trail of inventory state changes. */
    history: (encId: string) => `/skip/inventory/${encId}/history`,
  },
  skipCustomers: {
    list: '/skip/customers',
    detail: (encId: string) => `/skip/customers/${encId}`,
    create: '/skip/customers',
    update: (encId: string) => `/skip/customers/${encId}`,
    jobs: (encId: string) => `/skip/customers/${encId}/jobs`,
    checkDuplicates: '/skip/customers/check-duplicates',
  },
  skipSizes: {
    list: '/reference/skip-sizes',
  },
  // Skip stock types + sizes — per-tenant CRUD on the new
  // skip_stock_types / skip_stock_sizes catalogue (replaces the legacy
  // skip_sizes table). See server/src/modules/skip/stock/router.ts.
  skipStock: {
    types: '/skip/stock/types',
    type: (encId: string) => `/skip/stock/types/${encodeURIComponent(encId)}`,
    sizes: (typeEncId: string) =>
      `/skip/stock/types/${encodeURIComponent(typeEncId)}/sizes`,
    size: (typeEncId: string, sizeEncId: string) =>
      `/skip/stock/types/${encodeURIComponent(typeEncId)}/sizes/${encodeURIComponent(sizeEncId)}`,
    activateGlobal: (encId: string) =>
      `/skip/stock/types/globals/${encodeURIComponent(encId)}/activate`,
  },
  // Skip job types — per-tenant CRUD on skip_job_types + nested
  // stages and actions (replaces the legacy job_type ENUM). Mirrors
  // skipStock contract. See server/src/modules/skip/jobTypes/router.ts.
  skipJobTypes: {
    list: '/skip/job-types',
    type: (encId: string) => `/skip/job-types/${encodeURIComponent(encId)}`,
    activateGlobal: (encId: string) =>
      `/skip/job-types/globals/${encodeURIComponent(encId)}/activate`,
    stages: (typeEncId: string) =>
      `/skip/job-types/${encodeURIComponent(typeEncId)}/stages`,
    stage: (typeEncId: string, stageEncId: string) =>
      `/skip/job-types/${encodeURIComponent(typeEncId)}/stages/${encodeURIComponent(stageEncId)}`,
    actions: (typeEncId: string) =>
      `/skip/job-types/${encodeURIComponent(typeEncId)}/actions`,
    action: (typeEncId: string, actionEncId: string) =>
      `/skip/job-types/${encodeURIComponent(typeEncId)}/actions/${encodeURIComponent(actionEncId)}`,
  },
  customerSites: {
    list: (customerEncId: string) => `/customers/${customerEncId}/sites`,
    create: (customerEncId: string) => `/customers/${customerEncId}/sites`,
    update: (customerEncId: string, siteEncId: string) =>
      `/customers/${customerEncId}/sites/${siteEncId}`,
    remove: (customerEncId: string, siteEncId: string) =>
      `/customers/${customerEncId}/sites/${siteEncId}`,
  },
  // Stage 6 — Weighbridge Management module. Mirrors the server mount
  // points under /api/v1/wb/* (see server/src/app.ts Stage 6 block).
  // Phase C1 scaffold: collection paths only — per-resource detail /
  // sub-resource builders land alongside their phase's hook file (e.g.
  // wb.tickets.weightEvents() arrives with the C10 hook).
  wb: {
    yards: '/wb/yards',
    yard: (encId: string) => `/wb/yards/${encodeURIComponent(encId)}`,
    weighbridges: '/wb/weighbridges',
    weighbridge: (encId: string) => `/wb/weighbridges/${encodeURIComponent(encId)}`,
    calibrations: (weighbridgeEncId: string) =>
      `/wb/weighbridges/${encodeURIComponent(weighbridgeEncId)}/calibrations`,
    cameras: '/wb/cameras',
    /** Phase C2 — per-camera CRUD on /wb/cameras/:enc_id. */
    camera: (encId: string) => `/wb/cameras/${encodeURIComponent(encId)}`,
    barriers: '/wb/barriers',
    /** Phase C2 — per-barrier CRUD on /wb/barriers/:enc_id. */
    barrier: (encId: string) => `/wb/barriers/${encodeURIComponent(encId)}`,
    agents: '/wb/agents',
    /** Phase C2 — per-agent detail / soft-delete. */
    agent: (encId: string) => `/wb/agents/${encodeURIComponent(encId)}`,
    /** Phase C2 — generates a one-shot pairing code for an existing
     *  agent record. Admin only. Returns `{ pairing_code, expires_at }`
     *  (plaintext code is only present in this response). */
    agentPairingCode: (encId: string) =>
      `/wb/agents/${encodeURIComponent(encId)}/pairing-code`,
    /** Phase C2 — marks the agent unpaired & wipes its pairing material.
     *  Admin only. */
    agentDeactivate: (encId: string) =>
      `/wb/agents/${encodeURIComponent(encId)}/deactivate`,
    /** S61 — queues an ANPR capture on the agent serving the given bridge.
     *  Returns 200 { queued: true } or 503 { error: 'Agent not connected' }. */
    triggerAnpr: '/wb/agent/trigger-anpr',
    /** S67f — sets the active ticket+weight-type on the agent so capture_on_weight fires. */
    setActiveTicket: '/wb/agent/set-active-ticket',
    /** S62c — staff GET/PATCH for the ANPR weight threshold on a weighbridge. */
    cameraConfig: (weighbridgeEncId: string) =>
      `/wb/weighbridges/${encodeURIComponent(weighbridgeEncId)}/camera-config`,
    /** S63 — ANPR recognition audit log for a weighbridge. */
    anprLog: (weighbridgeEncId: string) =>
      `/wb/weighbridges/${encodeURIComponent(weighbridgeEncId)}/anpr-log`,
    hauliers: '/wb/hauliers',
    /** Phase C3 — per-haulier CRUD on /wb/hauliers/:enc_id. */
    haulier: (encId: string) => `/wb/hauliers/${encodeURIComponent(encId)}`,
    vehicles: '/wb/vehicles',
    vehiclesSearch: '/wb/vehicles/search',
    /** Phase C3 Group 2 — per-vehicle CRUD on /wb/vehicles/:enc_id. */
    vehicle: (encId: string) => `/wb/vehicles/${encodeURIComponent(encId)}`,
    /** Phase C3 Group 2 — stored tare endpoints (POST sets, DELETE clears). */
    vehicleTare: (encId: string) =>
      `/wb/vehicles/${encodeURIComponent(encId)}/tare`,
    drivers: '/wb/drivers',
    /** Phase C3 Group 3 — per-driver CRUD on /wb/drivers/:enc_id. */
    driver: (encId: string) => `/wb/drivers/${encodeURIComponent(encId)}`,
    /** Phase C3 Group 3 — multipart signature upload (field name
     *  `signature`). Accepts PNG / JPEG / WEBP up to 2 MB. */
    driverSignature: (encId: string) =>
      `/wb/drivers/${encodeURIComponent(encId)}/signature`,
    products: '/wb/products',
    /** Phase C3 Group 4 — per-product CRUD on /wb/products/:enc_id. */
    product: (encId: string) => `/wb/products/${encodeURIComponent(encId)}`,
    /** Migration 169 — product grouping. List = globals (minus exclusions)
     *  + own custom; POST creates a custom group. */
    productGroups: '/wb/product-groups',
    productGroup: (encId: string) =>
      `/wb/product-groups/${encodeURIComponent(encId)}`,
    /** Opt out of / back in to a global group (POST = exclude, DELETE = include). */
    productGroupExclude: (encId: string) =>
      `/wb/product-groups/${encodeURIComponent(encId)}/exclude`,
    vatRates: '/wb/vat-rates',
    /** Phase C5 — per-row PATCH/DELETE on /wb/vat-rates/:enc_id. No
     *  GET /:enc_id detail endpoint exists; the list is authoritative
     *  (see WEIGHBRIDGE_SERVER_CONTRACT.md §C5). */
    vatRate: (encId: string) =>
      `/wb/vat-rates/${encodeURIComponent(encId)}`,
    // Phase C3 — WB-lensed customer list (collection). Same base path as
    // customerSettings; GET / returns customers merged with their WB
    // settings (paginated + searchable), GET /:enc_id returns one row.
    customers: '/wb/customer-settings',
    customerSettings: (customerEncId: string) =>
      `/wb/customer-settings/${encodeURIComponent(customerEncId)}`,
    companySettings: '/wb/company-settings',
    priceBands: '/wb/price-bands',
    /** Phase C4 Group 1 — per-price-band CRUD on /wb/price-bands/:enc_id. */
    priceBand: (encId: string) =>
      `/wb/price-bands/${encodeURIComponent(encId)}`,
    /** Phase C4 Group 1 — bulk-replace / list of band line rows. */
    priceBandLines: (encId: string) =>
      `/wb/price-bands/${encodeURIComponent(encId)}/lines`,
    contracts: '/wb/contracts',
    /** Phase C4 Group 2 — per-contract CRUD on /wb/contracts/:enc_id. */
    contract: (encId: string) => `/wb/contracts/${encodeURIComponent(encId)}`,
    /** Phase C4 Group 2 — bulk-replace contract lines via PUT (+ list via GET). */
    contractLines: (encId: string) =>
      `/wb/contracts/${encodeURIComponent(encId)}/lines`,
    pricingResolve: '/wb/pricing/resolve',
    // Files surface is shared with the rest of the app — `/files/presigned`
    // is referenced here for completeness (C10 doesn't add a new endpoint;
    // we already consume it via apiClient.get('/files/presigned') today).
    /** Multi-Bridge UI — connected bridge list (numeric id + enc_id + status)
     *  for the selector row. */
    liveBridges: '/wb/live/bridges',
    /** Multi-Bridge UI — declare camera-preview interest for a bridge so the
     *  server relays pause/resume to the serving agent. */
    liveCameraPreview: '/wb/live/camera-preview',
    tickets: '/wb/tickets',
    ticketsAwaitingSecond: '/wb/tickets/awaiting-second-weigh',
    /** Phase C9 — GET /wb/tickets/:enc_id for the ticket detail page. */
    ticket: (encId: string) => `/wb/tickets/${encodeURIComponent(encId)}`,
    /** Phase C9 — POST /wb/tickets/:enc_id/reissue. Body is empty;
     *  response is `{ ticket }` (a new draft ticket cloned from the
     *  voided original). C11 owns void / second-weigh; reissue is wired
     *  in C9 because it's the only state transition reachable from the
     *  read-only detail page (voided tickets). */
    ticketReissue: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/reissue`,
    /** Phase C10 — POST a single weight event (gross/tare/verify) onto
     *  an existing ticket. The server derives the gross_method/tare_method
     *  columns on `wb_tickets` from the per-event `method` field. */
    ticketWeightEvents: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/weight-events`,
    /** Phase C10 — POST a pre-uploaded MinIO key as a ticket snapshot
     *  (the client never uploads the image bytes through this endpoint;
     *  the on-prem agent owns image capture). */
    ticketSnapshots: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/snapshots`,
    /** Phase C10 — POST ticket completion (payment_method + paid_amount).
     *  Response carries `barrier_actions[]` for the local agent to act on
     *  plus an optional `credit_limit_warning` flag. */
    ticketComplete: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/complete`,
    /** Phase C10/C11 — POST void with a `{ void_reason }` body. C10 uses
     *  this for the "Abandon ticket" affordance on a partially-built
     *  draft; C11 wires the full void-from-completed flow. */
    ticketVoid: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/void`,
    /** Phase C12 — server-rendered PDF for one of the four WB document
     *  types (wb_ticket / wb_advice_note / wb_wtn / wb_hazardous_consignment).
     *  Response is a PDF binary; consume via apiClient.raw.get with
     *  responseType:'blob' (the standard JSON envelope is bypassed). */
    ticketPdf: (encId: string, templateType: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/pdf/${encodeURIComponent(templateType)}`,
    /** Post-C13 cleanup — manual advice-note resend. Refused for non-
     *  completed tickets. Body is empty. Response: `{ queued: boolean }`. */
    ticketSendAdviceNote: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/send-advice-note`,
    /** Per-ticket activity feed — reads wb_activity (mig 142), not the legacy
     *  activity_logs. Response: `{ activity, pagination }`. */
    ticketActivity: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/activity`,
    /** Migration 167 — per-ticket advisory lock. POST acquires (body
     *  `{ origin }`), DELETE self-releases, GET reads current status (lock
     *  DTO or null). */
    ticketLock: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/lock`,
    /** Migration 167 — POST renews the caller's lock (30s heartbeat). */
    ticketLockHeartbeat: (encId: string) =>
      `/wb/tickets/${encodeURIComponent(encId)}/lock/heartbeat`,
    /** Migration 167 — manager/admin list of all active ticket locks. */
    locks: '/wb/locks',
    /** Migration 167 — manager/admin force-release a ticket's lock. */
    lockForceRelease: (encId: string) =>
      `/wb/locks/${encodeURIComponent(encId)}`,
    stockBalances: '/wb/stock/balances',
    stockMovements: '/wb/stock/movements',
    stockAlerts: '/wb/stock-alerts',
    /** Phase C8 — dismiss one alert. POST body is empty. */
    stockAlertDismiss: (encId: string) =>
      `/wb/stock-alerts/${encodeURIComponent(encId)}/dismiss`,
    eaExport: '/wb/reports/ea-waste-export',
    // Dev-only SSE stream — mounted only when WB_MOCK_AGENT_MODE=true
    // server-side. Consumers should feature-check via the subscription
    // / build flag before connecting.
    mockAgentStream: (weighbridgeEncId: string) =>
      `/wb/mock-agent/stream/${encodeURIComponent(weighbridgeEncId)}`,
  },
  // Print templates (PRINT_TEMPLATE_PLAN.md §3). Server module:
  // `server/src/modules/printTemplates/printTemplates.router.ts`,
  // mounted at /api/v1/print-templates. The render endpoint returns
  // a binary PDF or HTML string (Accept-negotiated) and is therefore
  // called via apiClient.raw (envelope-bypass) — see
  // src/api/printTemplates.ts.
  printTemplates: {
    list: '/print-templates',
    detail: (type: string) => `/print-templates/${encodeURIComponent(type)}`,
    upsert: (type: string) => `/print-templates/${encodeURIComponent(type)}`,
    revert: (type: string) => `/print-templates/${encodeURIComponent(type)}`,
    render: (type: string) =>
      `/print-templates/${encodeURIComponent(type)}/render`,
  },
  // GeoLink — cross-module search/coverage surface backing the GeoLink
  // Nearby Bookings + Coverage Analysis pages.
  geolink: {
    nearby: '/geolink/nearby',
    coverage: '/geolink/coverage',
  },
  // S56 — Configurable Quick Links. GET returns the tenant's ordered list;
  // PUT replaces the entire list (bulk-save).
  quickLinks: {
    list: '/settings/quick-links',
    save: '/settings/quick-links',
  },
  // Batch 4a — Digital Waste Tracking (admin-only settings).
  dwtSettings: {
    get: '/settings/dwt',
    save: '/settings/dwt',
  },
} as const;
